import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSquad } from '../contexts/SquadContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { LoginModal } from './LoginModal';

const PENDING_SQUAD_CODE_KEY = 'pending_squad_code';

export function SquadJoinHandler() {
  const { user, loading: authLoading } = useAuth();
  const { joinSquad, currentSquad } = useSquad();
  const { t } = useLanguage();
  const { showToast } = useToast();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const processedRef = useRef<string | null>(null);

  // Extract squad code from URL
  const getSquadCodeFromUrl = useCallback(() => {
    const path = window.location.pathname;
    const match = path.match(/\/squad\/([A-Z0-9]+)/i);
    return match ? match[1].toUpperCase() : null;
  }, []);

  // Handle squad join
  const handleJoinSquad = useCallback(async (code: string) => {
    if (!code || processedRef.current === code) return;
    processedRef.current = code;

    try {
      const { data, error } = await joinSquad(code);

      if (error) {
        showToast(error.message || t('common.unexpectedError'), 'error');
        processedRef.current = null;
      } else if (data) {
        showToast(t('squad.joinSuccess'), 'success');
        // Clear pending code from localStorage
        localStorage.removeItem(PENDING_SQUAD_CODE_KEY);
        // Update URL to remove the squad code
        window.history.replaceState({}, '', '/');
      }
    } catch (error) {
      showToast(t('common.unexpectedError'), 'error');
      processedRef.current = null;
    }
  }, [joinSquad, t, showToast]);

  // Track previous user state to detect login/signup
  const prevUserRef = useRef(user);

  // Check URL on mount and when auth state changes
  useEffect(() => {
    if (authLoading) return;

    const urlCode = getSquadCodeFromUrl();
    const pendingCode = localStorage.getItem(PENDING_SQUAD_CODE_KEY);

    // If user just logged in/signed up (was null, now has user)
    const justLoggedIn = !prevUserRef.current && user;
    if (justLoggedIn) {
      // Reset processedRef to allow processing pending code
      processedRef.current = null;
    }
    prevUserRef.current = user;

    // If user is logged in
    if (user) {
      // If there's a pending code from before login/signup, use that
      if (pendingCode && processedRef.current !== pendingCode) {
        // Small delay to ensure squad context is ready after signup
        const timer = setTimeout(() => {
          handleJoinSquad(pendingCode);
        }, 1000);
        return () => {
          clearTimeout(timer);
        };
      }

      // If there's a code in URL and user is not already in this squad
      if (urlCode && processedRef.current !== urlCode) {
        // Check if user is already in this squad
        if (currentSquad?.code === urlCode) {
          // Already in this squad, just update URL
          window.history.replaceState({}, '', '/');
          processedRef.current = urlCode;
          return;
        }

        // Try to join
        handleJoinSquad(urlCode);
      }
    } else {
      // User is not logged in
      if (urlCode && processedRef.current !== urlCode) {
        // Store code for after login/signup
        localStorage.setItem(PENDING_SQUAD_CODE_KEY, urlCode);
        // Show login modal
        setShowLoginModal(true);
        // Update URL to remove the squad code
        window.history.replaceState({}, '', '/');
        processedRef.current = urlCode;
      }
    }
  }, [user, authLoading, currentSquad, getSquadCodeFromUrl, handleJoinSquad]);

  return (
    <>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          // Clear pending code if user closes modal
          localStorage.removeItem(PENDING_SQUAD_CODE_KEY);
        }}
      />
    </>
  );
}

